import { Role } from 'auth';
import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import styles from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderNavigation.module.css';
import { TaxReportHolder } from '@/modules/issuer-dashboard/components/holder-create/tabs/TaxReportHolder.tsx';
import {
  useHolderTaxReportByYearDataQuery,
  useRegisterTotalFundByYearQuery,
  useUpsertHolderTaxReportByYearDataMutation,
} from '@/modules/issuer-dashboard/queries/tax-reports.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { PageLayout, Spinner } from 'ui';
import { ArrowLeftIcon } from 'ui/icons';

const forbiddenSuffixes = ['_allocation_method', '_total_fund', '_total_value'];
interface TaxReportHolderEditLocationState {
  from?: { pathname: string; search?: string } | string;
  holderDisplayName?: string;
}

const normalizeToSchemaShape = (
  input: Record<string, unknown>,
  options?: { fillTotalFund?: number },
) => {
  const clone: Record<string, unknown> = JSON.parse(JSON.stringify(input || {}));
  const normalizedTotalFund =
    Number.isFinite(options?.fillTotalFund) && typeof options?.fillTotalFund === 'number'
      ? String(options.fillTotalFund)
      : '0';

  const recurse = (curr: Record<string, unknown>) => {
    Object.keys(curr).forEach(key => {
      const value = curr[key];

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        recurse(value as Record<string, unknown>);
      }

      const isBaseField =
        (typeof value === 'string' || typeof value === 'number') &&
        !forbiddenSuffixes.some(suffix => key.endsWith(suffix));

      if (!isBaseField) return;

      const baseValue = String(value ?? '0');
      curr[key] = baseValue;

      const allocationKey = `${key}_allocation_method`;
      const totalFundKey = `${key}_total_fund`;
      const totalValueKey = `${key}_total_value`;

      if (!(allocationKey in curr)) {
        curr[allocationKey] = 'manual';
      }
      if (!(totalFundKey in curr)) {
        curr[totalFundKey] = normalizedTotalFund;
      }
      if (!(totalValueKey in curr)) {
        curr[totalValueKey] = '0';
      }
    });
  };

  recurse(clone);
  return clone;
};

const IssuerTaxReportHolderEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as TaxReportHolderEditLocationState | null;
  const [hasUnsavedUserChanges, setHasUnsavedUserChanges] = useState(false);
  const { registerId = '0', entityId = '0' } = useParams();
  const [searchParams] = useSearchParams();

  const year = useMemo(() => {
    const raw = Number(searchParams.get('year') || 0);
    return Number.isFinite(raw) && raw > 0 ? raw : new Date().getFullYear();
  }, [searchParams]);

  const register_id = Number(registerId);
  const entity_id = Number(entityId);
  const { data: registerTotalFundItem, isLoading: isLoadingRegisterTotalFund } =
    useRegisterTotalFundByYearQuery({ year, register_id });
  const registerTotalFund = useMemo(() => {
    const raw = Number(registerTotalFundItem?.total_fund ?? 0);
    return Number.isFinite(raw) ? raw : 0;
  }, [registerTotalFundItem?.total_fund]);

  const { data, isLoading, refetch } = useHolderTaxReportByYearDataQuery(
    {
      year,
      register_id,
      entity_id,
    },
    !register_id || !entity_id,
  );

  const initialJson = useMemo(() => {
    const raw = data?.tax_report_json;
    const isEmpty =
      !raw || (typeof raw === 'object' && !Array.isArray(raw) && !Object.keys(raw).length);
    const source = (isEmpty ? {} : raw) as Record<string, unknown>;
    return normalizeToSchemaShape(source, {
      fillTotalFund: isEmpty ? registerTotalFund : undefined,
    });
  }, [data?.tax_report_json, registerTotalFund]);

  const resolvedHolderTitleName = useMemo(() => {
    return locationState?.holderDisplayName?.trim() || '';
  }, [locationState?.holderDisplayName]);

  const { mutateAsync: upsertTaxReport, isLoading: isSaving } =
    useUpsertHolderTaxReportByYearDataMutation();

  const resolveBackDestination = useCallback(() => {
    const params = new URLSearchParams();
    params.set('register', String(register_id));
    params.set('tab-holder', 'Tax Reports');

    return {
      pathname: issuerDashboardPaths.dynamic.holder(String(entity_id)),
      search: params.toString(),
    };
  }, [entity_id, register_id]);

  const performBackNavigation = useCallback(() => {
    if (locationState?.from) {
      navigate(-1);
      return;
    }

    navigate(resolveBackDestination(), { replace: true });
  }, [locationState?.from, navigate, resolveBackDestination]);

  const handleBackClick = useCallback(() => {
    if (hasUnsavedUserChanges) {
      const shouldLeave = window.confirm(
        'Do you really want to leave this form? You will lose some entered information.',
      );
      if (!shouldLeave) return;
    }

    performBackNavigation();
  }, [hasUnsavedUserChanges, performBackNavigation]);

  const handleSave = async (taxReportJson: Record<string, unknown>) => {
    const normalizedPayload = normalizeToSchemaShape(taxReportJson);

    await upsertTaxReport({
      year,
      entity_id,
      register_id,
      tax_report_json: normalizedPayload,
    });
    await refetch();
    setHasUnsavedUserChanges(false);
    performBackNavigation();
  };

  const title = (
    <div className="flex gap-3 items-center">
      <button onClick={handleBackClick}>
        <img src={ArrowLeftIcon} alt="back" className="w-6 h-6 min-w-6 min-h-6" />
      </button>
      <span className="text-xl font-[RecifeDisplay] font-[600]">Edit tax report</span>
    </div>
  );

  return (
    <PageLayout title={title}>
      {(isLoading || isLoadingRegisterTotalFund) && (
        <div className="h-full w-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      {!isLoading && !isLoadingRegisterTotalFund && (
        <div className="w-full h-full flex justify-start items-start">
          <div className="w-full flex flex-col justify-start items-center">
            <div
              className={classNames(
                'w-full flex flex-col items-center max-h-full h-full gap-3 overflow-y-auto justify-start',
                'font-[Inter] text-[16px] leading-6 px-4 py-4',
                styles.scroll,
              )}
            >
              <TaxReportHolder
                entity_type_prefix=""
                allowSkip={false}
                externalData={{
                  entityId: entity_id,
                  year,
                  entityName: resolvedHolderTitleName,
                  initialTaxReportJson: initialJson,
                  isSaving,
                  registerTotalFund,
                  onSave: handleSave,
                  onDirtyChange: setHasUnsavedUserChanges,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(IssuerTaxReportHolderEdit, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
